/**
 * LooseRelationshipsList — renders the grouped list of loose-FK declarations
 * for a single (connection, database) meta-info record. JSON columns that
 * host multiple declarations (one per `sourcePath`) collapse into a single
 * card with sub-rows.
 */

import { Trash2, Filter, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProvenanceBadge } from './ProvenanceBadge';
import { qualifiedLabel } from '../../utils/looseFkResolver';
import type { LooseRelationship } from '@/types/electron';

export type LooseRelationshipsListProps = {
  relationships: LooseRelationship[];
  onRemove: (rel: LooseRelationship, groupLabel: string) => void;
  /**
   * Open the loose-FK editor dialog for this declaration. When omitted, the
   * Details button is hidden (legacy callers without an editor pathway). The
   * parent decides analytics labelling and any context plumbing.
   */
  onShowDetails?: (rel: LooseRelationship) => void;
  testIdPrefix?: string;
};

export function LooseRelationshipsList({
  relationships,
  onRemove,
  onShowDetails,
  testIdPrefix = 'metainfo',
}: LooseRelationshipsListProps) {
  if (relationships.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        No loose relationships declared yet.
      </div>
    );
  }

  const groups = (() => {
    const map = new Map<
      string,
      { schema: string; table: string; column: string; rels: LooseRelationship[] }
    >();
    for (const r of relationships) {
      const key = `${r.fromSchema}.${r.fromTable}.${r.fromColumn}`;
      let g = map.get(key);
      if (!g) {
        g = { schema: r.fromSchema, table: r.fromTable, column: r.fromColumn, rels: [] };
        map.set(key, g);
      }
      g.rels.push(r);
    }
    for (const g of map.values()) {
      g.rels.sort((a, b) => {
        const ap = a.sourcePath ?? '';
        const bp = b.sourcePath ?? '';
        if (ap === bp) return 0;
        if (!ap) return -1;
        if (!bp) return 1;
        return ap.localeCompare(bp);
      });
    }
    return Array.from(map.values());
  })();

  return (
    <>
      {groups.map((group) => {
        const groupKey = `${group.schema}.${group.table}.${group.column}`;
        return (
          <div
            key={groupKey}
            className="flex flex-col gap-1 border rounded p-2"
            data-testid={`${testIdPrefix}-group-${groupKey}`}
          >
            <div className="text-[11px] font-mono text-muted-foreground">
              {qualifiedLabel(group.schema, group.table, group.column)}
            </div>
            {group.rels.map((r) => {
              const pathLabel = r.sourcePath ?? '(whole column)';
              const label = `${qualifiedLabel(group.schema, group.table, group.column)}${
                r.sourcePath ? ` ${r.sourcePath}` : ''
              }`;
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between text-xs"
                  data-testid={`${testIdPrefix}-row-${r.id}`}
                >
                  <span translate="no" className="font-mono inline-flex items-center gap-1.5">
                    <span className="text-foreground/80">{pathLabel}</span>{' '}
                    → {qualifiedLabel(r.toSchema, r.toTable, r.toColumn)}
                    {(r.sourceFilter?.length || r.targetFilter?.length) ? (
                      <Filter
                        className="h-3 w-3 text-muted-foreground"
                        aria-label="Has filter"
                      />
                    ) : null}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <ProvenanceBadge provenance={r.provenance} />
                    {onShowDetails && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onShowDetails(r)}
                        title="Edit loose relationship"
                        aria-label="Edit loose relationship"
                        className="h-6 px-2 text-[11px]"
                        data-testid={`${testIdPrefix}-details-${r.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Details
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onRemove(r, label)}
                      aria-label="Remove relationship"
                      title="Remove relationship"
                      className="h-6 w-6 text-destructive"
                      data-testid={`${testIdPrefix}-remove-${r.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
